import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import AuthContext from '../context/AuthContext';
import vasstrOrangeLogo from '../media/vasstrOrangeLogo.png';
import Base from './Base';
import {
  CheckCircle2, Shield, Gift, Copy, Tag, ArrowRight, Check, X,
  Crown, CreditCard, Wallet, Sparkles, Users, Star, Headphones,
  Percent, MessageCircle
} from 'lucide-react';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';

const plans = [
  {
    months: 3,
    price: 6750,
    perMonth: 2250,
    name: "Starter",
    tagline: "3 months",
    hero: false,
    badge: null,
    savings: null,
    features: [
      "Branded storefront (yourbrand.vasstr.com)",
      "Upload unlimited products",
      "Order management & tracking",
      "Payment gateway (Razorpay)",
      "Basic analytics",
    ],
    exclusive: [],
  },
  {
    months: 6,
    price: 13000,
    perMonth: 2167,
    name: "Growth",
    tagline: "6 months",
    hero: false,
    badge: null,
    savings: "Save \u20B9500",
    features: [
      "Everything in Starter",
      "All 20+ premium templates",
      "Offline order book",
      "GST-compliant invoicing",
      "Full analytics dashboard",
      "Customer coupon system",
      "SEO tools & blog",
    ],
    exclusive: [],
  },
  {
    months: 12,
    price: 24000,
    perMonth: 2000,
    name: "Pro",
    tagline: "12 months",
    hero: true,
    badge: "RECOMMENDED",
    savings: "Save \u20B93,000",
    features: [
      "Everything in Growth",
      "Advanced SEO tools",
      "Customer coupon system",
      "Full analytics + Customer LTV",
      "Place a Bid feature",
    ],
    exclusive: [
      { text: "FREE store setup by our team", icon: Crown },
      { text: "Direct WhatsApp support", icon: MessageCircle },
      { text: "Priority feature requests", icon: Star },
    ],
  },
];


const Subscription = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    is_active: false, expiry_date: null, amount: 0.0,
    plan: null, has_used_free_trial: false,
    credits: { total: 0, items: [] },
    referral: { code: '', referrals_count: 0, credit_per_referral: 500 },
  });
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [useCredits, setUseCredits] = useState(false);
  const [referralInput, setReferralInput] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const { authToken } = useContext(AuthContext);
  const orderRef = useRef(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  const getHeaders = () => ({
    headers: {
      Authorization: `Bearer ${authToken?.access}`,
      'Content-Type': 'application/json',
    },
  });

  const fetchSubscriptionStatus = async () => {
    if (!authToken) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/get_subscription_status/`, getHeaders()
      );
      setSubscriptionStatus(response.data);
    } catch (err) {
      devLog.error('Failed to fetch subscription status:', err);
    }
  };

  useEffect(() => {
    if (authToken) fetchSubscriptionStatus();
  }, [authToken]);

  useEffect(() => {
    setPromoResult(null);
    setPromoCode('');
  }, [selectedPlan]);

  // Auto-scroll to order summary when a plan is selected
  useEffect(() => {
    if (selectedPlan !== null && !subscriptionStatus.is_active) {
      setTimeout(() => {
        orderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [selectedPlan]);

  const handleApplyPromo = async () => {
    if (!promoCode.trim() || selectedPlan === null) return;
    setPromoLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/validate_subscription_promo/`,
        { code: promoCode, plan: selectedPlan },
        getHeaders()
      );
      if (response.data.success) {
        setPromoResult(response.data.data);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Invalid promo code';
      Swal.fire({ icon: 'error', title: 'Invalid Code', text: msg, confirmButtonColor: '#d45614' });
      setPromoResult(null);
    }
    setPromoLoading(false);
  };

  const handleApplyReferral = async () => {
    if (!referralInput.trim()) return;
    setReferralLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/apply_referral_code/`,
        { referral_code: referralInput },
        getHeaders()
      );
      if (response.data.success) {
        Swal.fire({
          icon: 'success', title: 'Referral Applied!',
          text: response.data.message,
          confirmButtonColor: '#d45614'
        });
        setReferralInput('');
        await fetchSubscriptionStatus();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Invalid referral code';
      Swal.fire({ icon: 'error', title: 'Error', text: msg, confirmButtonColor: '#d45614' });
    }
    setReferralLoading(false);
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(subscriptionStatus.referral?.code || '');
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleFreeTrialActivation = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/activate_free_trial/`, {}, getHeaders());
      Swal.fire({
        title: 'Welcome to Vasstr!',
        html: `<p style="color:#4b5563">Your 1-month FREE trial is now active.</p>
          <p style="color:#4b5563;margin-top:8px">Start uploading your designs and customizing your store!</p>
          <a href="/homepage" style="display:inline-block;margin-top:16px;background:#d45614;color:white;font-weight:600;padding:10px 24px;border-radius:8px;text-decoration:none">
            Go to Dashboard
          </a>`,
        icon: 'success', showConfirmButton: false, showCloseButton: true,
      });
      await fetchSubscriptionStatus();
      setSelectedPlan(null);
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Failed to activate free trial.', 'error');
    }
  };

  const handleSubscriptionSubmit = async () => {
    if (selectedPlan === null) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/save_subscription/`,
        {
          plan: selectedPlan,
          promo_code: promoResult ? promoCode : '',
          use_credits: useCredits,
        },
        getHeaders()
      );

      if (response.data.fully_covered) {
        Swal.fire({
          title: 'Subscription Activated!',
          html: `<p style="color:#4b5563">Your credits covered the full amount.</p>
            <a href="/homepage" style="display:inline-block;margin-top:16px;background:#d45614;color:white;font-weight:600;padding:10px 24px;border-radius:8px;text-decoration:none">
              Go to Dashboard
            </a>`,
          icon: 'success', showConfirmButton: false, showCloseButton: true,
        });
        await fetchSubscriptionStatus();
        setSelectedPlan(null);
        return;
      }

      const { razorpay_order_id, razorpay_key, amount, plan } = response.data;

      const options = {
        key: razorpay_key,
        amount: amount * 100,
        currency: 'INR',
        name: 'VASSTR',
        image: vasstrOrangeLogo,
        description: `Subscription: ${plan} Month${plan > 1 ? 's' : ''}`,
        order_id: razorpay_order_id,
        handler: async (rzpResponse) => {
          try {
            await axios.post(
              `${API_BASE_URL}/api/verify_payment/`,
              {
                plan: selectedPlan,
                razorpay_payment_id: rzpResponse.razorpay_payment_id,
                razorpay_order_id: rzpResponse.razorpay_order_id,
                razorpay_signature: rzpResponse.razorpay_signature,
                promo_code: promoResult ? promoCode : '',
                use_credits: useCredits,
              },
              getHeaders()
            );
            Swal.fire({
              title: 'Subscription Activated!',
              html: `<p style="color:#4b5563">Payment verified successfully.</p>
                <a href="/homepage" style="display:inline-block;margin-top:16px;background:#d45614;color:white;font-weight:600;padding:10px 24px;border-radius:8px;text-decoration:none">
                  Go to Dashboard
                </a>`,
              icon: 'success', showConfirmButton: false, showCloseButton: true,
            });
            await fetchSubscriptionStatus();
            setSelectedPlan(null);
          } catch (err) {
            Swal.fire('Error', 'Payment verification failed. Contact support if amount was debited.', 'error');
          }
        },
        theme: { color: '#d45614' },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        Swal.fire('Payment Failed', response.error.description, 'error');
      });
      rzp.open();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Failed to initiate payment.', 'error');
    }
  };

  const formatExpiryDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata'
    });
  };

  const fmt = (price) => new Intl.NumberFormat('en-IN').format(price);

  const getSelectedPlanData = () => plans.find(p => p.months === selectedPlan);

  const calculateTotal = () => {
    const plan = getSelectedPlanData();
    if (!plan) return { base: 0, discount: 0, credits: 0, gst: 0, total: 0 };

    const base = plan.price;
    const discount = promoResult ? promoResult.discount_amount : 0;
    const afterDiscount = base - discount;

    let creditAmount = 0;
    if (useCredits && subscriptionStatus.credits?.total > 0) {
      creditAmount = Math.min(subscriptionStatus.credits.total, afterDiscount);
    }

    const afterCredits = afterDiscount - creditAmount;
    const gst = Math.round(afterCredits * 0.18);
    const total = afterCredits + gst;

    return { base, discount, credits: creditAmount, gst, total: Math.max(0, total) };
  };

  const isActive = subscriptionStatus.is_active;
  const totals = calculateTotal();
  const hasCredits = subscriptionStatus.credits?.total > 0;
  const referralCode = subscriptionStatus.referral?.code;

  return (
    <Base Content={
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12">

          {/* ===== ACTIVE SUBSCRIPTION ===== */}
          {isActive && (
            <div className="mb-8 bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="bg-emerald-600 px-5 py-3.5 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-white" />
                <div>
                  <p className="text-white font-semibold text-sm">Your Subscription is Active</p>
                  <p className="text-emerald-100 text-xs">
                    {subscriptionStatus.plan > 0 ? `${subscriptionStatus.plan} Month Plan` : 'Free Trial'} · Expires {formatExpiryDate(subscriptionStatus.expiry_date)}
                  </p>
                </div>
              </div>
              <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-gray-600">Credits:</span>
                    <span className="font-bold text-gray-900">{'\u20B9'}{fmt(subscriptionStatus.credits?.total || 0)}</span>
                  </div>
                  <div className="w-px h-5 bg-gray-200 hidden sm:block" />
                  {referralCode && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Your code:</span>
                      <span className="font-mono font-bold text-gray-900">{referralCode}</span>
                      <button
                        onClick={copyReferralCode}
                        className={`text-xs px-2 py-0.5 rounded font-medium transition ${
                          copySuccess ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-[#d45614] hover:bg-orange-200'
                        }`}
                      >
                        {copySuccess ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400">Share your code, both earn {'\u20B9'}500</p>
              </div>
            </div>
          )}

          {/* ===== HEADING ===== */}
          <div className="text-center mb-8 sm:mb-10">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              Choose Your Plan
            </h1>
            <p className="text-gray-500 mt-2 text-sm sm:text-base">
              Start building your brand's online store
            </p>
          </div>

          {/* ===== 3 PLAN CARDS ===== */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 mb-6 items-start">
            {plans.map((plan) => {
              const isSelected = selectedPlan === plan.months;
              const isHero = plan.hero;
              const disabled = isActive;

              return (
                <div
                  key={plan.months}
                  className={`
                    relative bg-white rounded-2xl flex flex-col transition-all duration-200
                    ${disabled ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
                    ${isHero
                      ? isSelected
                        ? 'border-2 border-[#d45614] ring-2 ring-[#d45614]/30 shadow-xl md:scale-105 md:-my-3'
                        : 'border-2 border-[#d45614]/40 shadow-lg md:scale-105 md:-my-3'
                      : isSelected
                        ? 'border-2 border-gray-800 ring-2 ring-gray-800/20 shadow-lg'
                        : 'border border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }
                  `}
                  onClick={() => !disabled && setSelectedPlan(plan.months)}
                >
                  {/* Badge */}
                  {plan.badge && (
                    <div className="bg-[#d45614] text-white text-[11px] font-bold px-4 py-1.5 text-center rounded-t-2xl flex items-center justify-center gap-1.5 tracking-wide">
                      <Sparkles className="w-3.5 h-3.5" />
                      {plan.badge}
                    </div>
                  )}

                  <div className={`p-5 sm:p-6 flex flex-col flex-1 ${plan.badge ? '' : 'pt-6'}`}>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                      {isSelected && (
                        <div className={`${isHero ? 'bg-[#d45614]' : 'bg-gray-800'} text-white rounded-full p-0.5`}>
                          <Check className="w-3.5 h-3.5" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mb-4">{plan.tagline}</p>

                    {/* Price */}
                    <div className="mb-5">
                      <div className="flex items-baseline gap-1">
                        <span className="text-sm text-gray-400">{'\u20B9'}</span>
                        <span className={`text-4xl font-extrabold ${isHero ? 'text-[#d45614]' : 'text-gray-900'}`}>
                          {fmt(plan.price)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">
                          {'\u20B9'}{fmt(plan.perMonth)}/month + GST
                        </span>
                        {plan.savings && (
                          <span className="text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                            {plan.savings}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-2.5 mb-5 flex-grow">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Exclusive features (Pro only) */}
                    {plan.exclusive.length > 0 && (
                      <div className="border-t border-dashed border-orange-200 pt-4 mb-5 space-y-2.5">
                        <p className="text-[10px] font-bold text-[#d45614] uppercase tracking-widest">Only in Pro</p>
                        {plan.exclusive.map((ex, i) => {
                          const Icon = ex.icon;
                          return (
                            <li key={i} className="flex items-start gap-2 text-sm font-semibold text-gray-800 list-none">
                              <Icon className="w-4 h-4 text-[#d45614] mt-0.5 flex-shrink-0" />
                              <span>{ex.text}</span>
                            </li>
                          );
                        })}
                      </div>
                    )}

                    {/* CTA Button */}
                    <button
                      className={`w-full py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                        isSelected
                          ? isHero
                            ? 'bg-[#d45614] text-white shadow-md'
                            : 'bg-gray-900 text-white shadow-md'
                          : isHero
                            ? 'bg-[#d45614] text-white hover:bg-[#b3470f] shadow-sm'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!disabled) setSelectedPlan(plan.months);
                      }}
                    >
                      {isSelected ? (
                        <><CheckCircle2 className="w-4 h-4" /> Selected</>
                      ) : isHero ? (
                        <>Get Started <ArrowRight className="w-4 h-4" /></>
                      ) : (
                        <>Select {plan.name}</>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ===== FREE TRIAL CARD ===== */}
          {!isActive && !subscriptionStatus.has_used_free_trial && (
            <div className="max-w-lg mx-auto mb-8">
              <div
                className={`bg-white rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 transition-all duration-200 cursor-pointer ${
                  selectedPlan === 0
                    ? 'border-2 border-emerald-500 ring-2 ring-emerald-500/20 shadow-lg'
                    : 'border border-gray-200 hover:border-emerald-300 hover:shadow-md'
                }`}
                onClick={() => setSelectedPlan(0)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg font-bold text-gray-900">Free Trial</span>
                    <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">30 DAYS</span>
                  </div>
                  <p className="text-xs text-gray-500">Full platform access. No card required. No commitment.</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFreeTrialActivation();
                  }}
                  className={`px-6 py-2.5 rounded-xl text-sm font-bold transition whitespace-nowrap ${
                    selectedPlan === 0
                      ? 'bg-emerald-600 text-white shadow-md'
                      : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  }`}
                >
                  Start Free Trial
                </button>
              </div>
            </div>
          )}

          {/* Hint when no plan selected */}
          {!isActive && selectedPlan === null && (
            <p className="text-center text-xs text-gray-400 mb-10 flex items-center justify-center gap-1.5">
              <Tag className="w-3.5 h-3.5" />
              Have a promo code? Select a plan to apply it
            </p>
          )}

          {/* ===== ORDER SUMMARY ===== */}
          {selectedPlan !== null && !isActive && (
            <div ref={orderRef} className="max-w-lg mx-auto mb-10">
              <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm p-5 sm:p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-base text-gray-900">Order Summary</h3>
                  <span className="text-xs bg-gray-100 text-gray-600 font-medium px-3 py-1 rounded-full">
                    {getSelectedPlanData()?.name} · {selectedPlan} months
                  </span>
                </div>

                {/* Promo Code */}
                <div className="bg-gray-50 rounded-xl p-3.5">
                  <label className="text-xs font-semibold text-gray-500 mb-2 block uppercase tracking-wide">
                    Promo Code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="e.g. EARLYBIRD50"
                      className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#d45614]/50 focus:border-[#d45614] bg-white"
                      disabled={!!promoResult}
                    />
                    {promoResult ? (
                      <button
                        onClick={() => { setPromoResult(null); setPromoCode(''); }}
                        className="px-3 py-2.5 bg-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-300 transition flex items-center gap-1"
                      >
                        <X className="w-3.5 h-3.5" /> Remove
                      </button>
                    ) : (
                      <button
                        onClick={handleApplyPromo}
                        disabled={promoLoading || !promoCode.trim()}
                        className="px-4 py-2.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 transition disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        {promoLoading ? '...' : 'Apply'}
                      </button>
                    )}
                  </div>
                  {promoResult && (
                    <div className="mt-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-700 flex items-center gap-1.5 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                      {promoResult.message}
                    </div>
                  )}
                </div>

                {/* Credits */}
                {hasCredits && (
                  <label className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useCredits}
                      onChange={(e) => setUseCredits(e.target.checked)}
                      className="w-4 h-4 text-[#d45614] rounded border-gray-300 focus:ring-[#d45614]"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-amber-800">
                        Use {'\u20B9'}{fmt(subscriptionStatus.credits.total)} credits
                      </span>
                      <p className="text-xs text-amber-600">From referrals & promotions</p>
                    </div>
                  </label>
                )}

                {/* Price Breakdown */}
                <div className="pt-3 space-y-2 border-t border-gray-100">
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>{getSelectedPlanData()?.name} ({selectedPlan} months)</span>
                    <span>{'\u20B9'}{fmt(totals.base)}</span>
                  </div>
                  {totals.discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600 font-medium">
                      <span>Discount ({promoResult?.code})</span>
                      <span>- {'\u20B9'}{fmt(totals.discount)}</span>
                    </div>
                  )}
                  {totals.credits > 0 && (
                    <div className="flex justify-between text-sm text-amber-600 font-medium">
                      <span>Credits</span>
                      <span>- {'\u20B9'}{fmt(totals.credits)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>GST (18%)</span>
                    <span>{'\u20B9'}{fmt(totals.gst)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-extrabold text-gray-900 pt-3 border-t border-gray-100">
                    <span>Total</span>
                    <span>{'\u20B9'}{fmt(totals.total)}</span>
                  </div>
                </div>

                {/* Pay Button */}
                <button
                  onClick={handleSubscriptionSubmit}
                  className="w-full py-3.5 bg-[#d45614] text-white font-bold text-sm rounded-xl hover:bg-[#b3470f] transition-all shadow-lg shadow-orange-200/50 hover:shadow-xl flex items-center justify-center gap-2"
                >
                  {totals.total <= 0 ? (
                    <><Wallet className="w-4 h-4" /> Activate with Credits</>
                  ) : (
                    <><CreditCard className="w-4 h-4" /> Pay {'\u20B9'}{fmt(totals.total)}</>
                  )}
                </button>

                <p className="text-[11px] text-center text-gray-400 flex items-center justify-center gap-1">
                  <Shield className="w-3 h-3" /> Razorpay secured · 30-day money-back guarantee
                </p>
              </div>
            </div>
          )}

          {/* ===== REFERRAL SECTION ===== */}
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
              <h3 className="font-bold text-base text-gray-900 flex items-center gap-2 mb-3">
                <Gift className="w-5 h-5 text-[#d45614]" /> Refer & Earn
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Share your code with other designers. When they subscribe, both of you get <b>{'\u20B9'}500 credit</b>.
              </p>

              {/* Your referral code */}
              {referralCode && (
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs text-gray-400">Your code:</span>
                  <span className="font-mono font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-sm tracking-wider flex-1 text-center">
                    {referralCode}
                  </span>
                  <button
                    onClick={copyReferralCode}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1 ${
                      copySuccess
                        ? 'bg-green-100 text-green-700'
                        : 'bg-[#d45614] text-white hover:bg-[#b3470f]'
                    }`}
                  >
                    {copySuccess ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
              )}

              {subscriptionStatus.referral?.referrals_count > 0 && (
                <p className="text-xs text-gray-400 mb-4 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {subscriptionStatus.referral.referrals_count} referred · {'\u20B9'}{fmt(subscriptionStatus.referral.referrals_count * 500)} earned
                </p>
              )}

              {/* Apply someone else's code */}
              <div className="border-t border-gray-100 pt-4">
                <label className="text-xs font-semibold text-gray-500 mb-2 block uppercase tracking-wide">
                  Have a referral code?
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={referralInput}
                    onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                    placeholder="e.g. NIHA-REF"
                    className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#d45614]/50 focus:border-[#d45614] font-medium"
                  />
                  <button
                    onClick={handleApplyReferral}
                    disabled={referralLoading || !referralInput.trim()}
                    className="px-4 py-2.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 transition disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {referralLoading ? '...' : 'Apply'}
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    }/>
  );
};

export default Subscription;
